import { useLayoutEffect, useRef, useState } from 'react'
import { niceScale } from '../inventoree/EndpointCharts'

/**
 * Graficul "Device Working Time" al tabloului Productivitee, desenat de mana
 * in SVG.
 *
 * -----------------------------------------------------------------------------
 * DE CE E DESENAT AICI SI NU ADUS DINTR-O BIBLIOTECA
 * -----------------------------------------------------------------------------
 *
 * Fiindca desenul asta era amCharts (`ProductiviteeDashboard.amChartSimpleLine`
 * din `public/js/productivitee.js`), iar amCharts are licenta comerciala si a
 * iesit din portal sub auditul bancar din septembrie 2026, cu doua probe
 * incuiate dupa el. Odata cu biblioteca au plecat si cele trei desene ale
 * tabloului, si capetele care le hraneau - de aici "lipsesc grafice". Nu se
 * intorc pe usa din dos a altei biblioteci: v4 isi deseneaza graficele singur,
 * ca `alarms/AreaChart.tsx`, `devices/AgentMonitorChart.tsx` si
 * `inventoree/EndpointCharts.tsx`.
 *
 * FORMA E CEA VECHE, nu una noua: coloane albastre pentru timpul ACTIV, o linie
 * rosie cu bulina peste ele pentru cel INACTIV, si axa din stanga in durate
 * ("08h 20m 00s"), nu in secunde goale - `duration: 'ss'` al axei vechi.
 * Etichetele de jos se rarefiaza la cel mult zece, drepte si NICIODATA rupte:
 * `autoWrap` sparge un nume de masina litera cu litera cand nu incape, si
 * manca jumatate din inaltimea desenului. Vezi comentariul din foaia veche.
 *
 * Cele doua desene de coloane - Top Productive / Top Unproductive - NU sunt
 * aici: alea sunt `VerticalBarChart` din `inventoree/EndpointCharts.tsx`,
 * acelasi grafic cu aceeasi asezare a etichetelor. Al treilea bar vertical al
 * portalului ar fi fost al treilea dialect pentru acelasi lucru.
 */

const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif'
const AXIS = '#666'
const GRID = 'rgba(0,0,0,0.1)'

/** Culorile desenului vechi: `$fillColor = '#0998F7'` pentru coloane, `#DE4C4F` pentru linie. */
export const WORK_COLORS = { active: '#0998F7', inactive: '#DE4C4F' } as const

export interface WorkTimeRow {
  name: string
  active_time: number
  inactive_time: number
}

/**
 * Durata scrisa pe axa, ca `durationUnits: {hh: 'h ', mm: 'm ', ss: 's'}`:
 * "08h 20m 00s". Ceasul, nu `durationString` din Easeedesk - acolo zecile nu
 * sunt umplute cu zero, si treptele unei axe nu s-ar mai citi una sub alta.
 */
export function axisDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds))
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(Math.floor(whole / 3600))}h ${pad(Math.floor((whole % 3600) / 60))}m ${pad(whole % 60)}s`
}

/** Latimea panoului, masurata - aceeasi socoteala ca `useWidth` din EndpointCharts. */
function useWidth(fallback = 300) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return [box, width > 0 ? width : fallback] as const
}

/**
 * Cate etichete se scriu jos, si la ce pas.
 *
 * `labelFunction` din graficul vechi: cel mult zece, alese la pas egal; restul
 * se citesc din balonul de sub cursor.
 */
export function labelStep(count: number): number {
  return Math.max(1, Math.ceil(count / 10))
}

const MAX_TICK_CHARS = 18

function tick(label: string): string {
  return label.length > MAX_TICK_CHARS ? `${label.slice(0, MAX_TICK_CHARS - 1)}…` : label
}

export function WorkTimeChart({ rows, height = 260 }: { rows: WorkTimeRow[]; height?: number }) {
  const [box, width] = useWidth()
  const [hover, setHover] = useState<number | null>(null)

  /*
   * Etichetele stau inclinate la 45 de grade, deci cer jos cat sunt de lungi
   * impartit la radical din doi - aceeasi socoteala ca `VerticalBarChart`.
   * Cu inaltime fixa, un nume de cincisprezece litere iesea taiat pe sub
   * grafic. La stanga tin loc duratele ("08h 20m 00s"), nu cifre goale.
   */
  const longest = Math.max(0, ...rows.map((r) => Math.min(r.name.length, MAX_TICK_CHARS))) * 5.6
  const pad = { top: 12, right: 12, bottom: Math.min(120, Math.round(24 + longest * 0.71)), left: 86 }
  const plotLeft = pad.left
  const plotRight = Math.max(plotLeft + 1, width - pad.right)
  const plotTop = pad.top
  const plotBottom = Math.max(plotTop + 1, height - pad.bottom)
  const plotW = Math.max(1, plotRight - plotLeft)
  const plotH = Math.max(1, plotBottom - plotTop)

  const max = Math.max(0, ...rows.map((r) => Math.max(r.active_time, r.inactive_time)))
  const { top, step } = niceScale(max)
  const band = plotW / Math.max(1, rows.length)
  const barW = Math.min(64, band * 0.8)
  const y = (v: number) => plotBottom - (v / top) * plotH
  const cx = (i: number) => plotLeft + band * i + band / 2

  const gridlines: number[] = []
  for (let v = 0; v <= top + 1e-9; v += step) gridlines.push(Number(v.toFixed(6)))
  const everyNth = labelStep(rows.length)

  /* Linia inactivului: un singur drum, cu suprafata sub el la 10% - `fillAlphas: 0.1`. */
  const line = rows.map((r, i) => `${cx(i)},${y(r.inactive_time)}`).join(' ')
  const area = rows.length === 0 ? '' : `${plotLeft + band / 2},${plotBottom} ${line} ${cx(rows.length - 1)},${plotBottom}`

  const tipRow = hover === null ? null : rows[hover]

  return (
    <div ref={box} style={{ width: '100%', height, position: 'relative' }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Device working time">
          {gridlines.map((v) => (
            <g key={v}>
              <line x1={plotLeft} x2={plotRight} y1={y(v)} y2={y(v)} stroke={GRID} strokeWidth={1} />
              <text x={plotLeft - 6} y={y(v) + 4} textAnchor="end" style={{ fill: AXIS, fontSize: 11, fontFamily: FONT }}>
                {axisDuration(v)}
              </text>
            </g>
          ))}
          {rows.map((r, i) => (
            <rect
              key={`bar-${r.name}-${i}`}
              x={cx(i) - barW / 2}
              y={y(r.active_time)}
              width={barW}
              height={Math.max(0, plotBottom - y(r.active_time))}
              fill={WORK_COLORS.active}
              fillOpacity={0.4}
              stroke={WORK_COLORS.active}
              strokeOpacity={0.8}
            />
          ))}
          {rows.length > 0 && (
            <>
              <polygon points={area} fill={WORK_COLORS.inactive} fillOpacity={0.1} />
              <polyline points={line} fill="none" stroke={WORK_COLORS.inactive} strokeWidth={2} strokeOpacity={0.8} />
              {rows.map((r, i) => (
                <circle
                  key={`dot-${r.name}-${i}`}
                  cx={cx(i)}
                  cy={y(r.inactive_time)}
                  r={3.5}
                  fill="#fff"
                  stroke={WORK_COLORS.inactive}
                  strokeWidth={2}
                />
              ))}
            </>
          )}
          {rows.map((r, i) =>
            i % everyNth === 0 ? (
              <text
                key={`tick-${r.name}-${i}`}
                x={cx(i)}
                y={plotBottom + 8}
                textAnchor="end"
                transform={`rotate(-45 ${cx(i)} ${plotBottom + 8})`}
                style={{ fill: AXIS, fontSize: 10, fontFamily: FONT }}
              >
                {tick(r.name)}
              </text>
            ) : null,
          )}
          {/* Banda de sub cursor: numele si cele doua durate, si pentru masinile
              fara eticheta scrisa jos. */}
          {rows.map((r, i) => (
            <rect
              key={`hit-${r.name}-${i}`}
              x={plotLeft + band * i}
              y={plotTop}
              width={band}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>
      )}
      {tipRow && hover !== null && (
        <div
          className="productivitee-chart-tip"
          style={{
            position: 'absolute',
            left: Math.min(Math.max(4, cx(hover) + 10), Math.max(4, width - 170)),
            top: Math.max(2, y(Math.max(tipRow.active_time, tipRow.inactive_time)) - 54),
            background: 'rgba(0,0,0,0.8)',
            color: '#fff',
            borderRadius: 6,
            padding: '4px 8px',
            fontFamily: FONT,
            fontSize: 12,
            lineHeight: '16px',
            pointerEvents: 'none',
            maxWidth: 180,
            zIndex: 5,
          }}
        >
          <div style={{ fontWeight: 'bold' }}>{tipRow.name}</div>
          <div>Active time: {axisDuration(tipRow.active_time)}</div>
          <div>Inactive time: {axisDuration(tipRow.inactive_time)}</div>
        </div>
      )}
    </div>
  )
}
